import type { LabConfig, TestResult } from '../types';
import { loadWasmBoundary } from '../wasm';
import { computeStats, now } from '../utils';

const GROUP = 'I_wasm_boundary';

function measureWork(work: () => void, options: { runs: number; warmup: number }): number[] {
  const { runs, warmup } = options;
  for (let i = 0; i < warmup; i++) {
    work();
  }
  const samples: number[] = [];
  for (let i = 0; i < runs; i++) {
    const t0 = now();
    work();
    const t1 = now();
    samples.push(t1 - t0);
  }
  return samples;
}

function makeResult(
  name: string,
  samples: number[],
  extra?: Record<string, unknown>
): TestResult {
  return {
    name,
    group: GROUP,
    success: samples.length > 0,
    durationMs: samples.reduce((a, b) => a + b, 0),
    samples,
    stats: computeStats(samples),
    metadata: extra,
  };
}

export async function runWasmBoundaryTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];

  if (!config.enableWASM) {
    return tests;
  }

  const isProd = config.mode === 'prod';
  const runs = isProd ? 5 : 12;
  const warmup = isProd ? 1 : 2;

  let wasm: Awaited<ReturnType<typeof loadWasmBoundary>>;
  try {
    wasm = await loadWasmBoundary('/boundary.wasm');
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    return [
      {
        name: 'boundary_load',
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: `Boundary WASM load failed: ${error}`,
      },
    ];
  }

  // Scale iterations so each measurement stays timer-resolvable.
  // call_known_* are kept as light calls but the result is accumulated to
  // prevent the engine from eliminating the calls as dead code.
  const w = {
    wasmToJs: isProd ? 50_000 : 250_000,
    known0: isProd ? 1_000_000 : 5_000_000,
    known1: isProd ? 1_000_000 : 5_000_000,
    known2: isProd ? 1_000_000 : 5_000_000,
    callGeneric2: isProd ? 50_000 : 250_000,
    getterSetter: isProd ? 50_000 : 250_000,
    applyCall: isProd ? 50_000 : 250_000,
    ifAdd: isProd ? 100_000 : 500_000,
  };

  try {
    const samples = measureWork(() => wasm.wasmToJs(w.wasmToJs), { runs, warmup });
    tests.push(makeResult('wasm_to_js', samples));
  } catch (err) {
    tests.push({
      name: 'wasm_to_js',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      let sink = 0;
      for (let i = 0; i < w.known0; i++) {
        sink += wasm.known0();
      }
      if (sink !== w.known0 * 42) {
        console.error('call_known_0 checksum mismatch', sink);
      }
    }, { runs, warmup });
    tests.push(makeResult('call_known_0', samples, { iterations: w.known0 }));
  } catch (err) {
    tests.push({
      name: 'call_known_0',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      let sink = 0;
      for (let i = 0; i < w.known1; i++) {
        sink += wasm.known1(i);
      }
      if (sink !== w.known1 * (w.known1 + 1) / 2 + w.known1) {
        console.error('call_known_1 checksum mismatch', sink);
      }
    }, { runs, warmup });
    tests.push(makeResult('call_known_1', samples, { iterations: w.known1 }));
  } catch (err) {
    tests.push({
      name: 'call_known_1',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      let sink = 0;
      for (let i = 0; i < w.known2; i++) {
        sink += wasm.known2(i, i + 1);
      }
      if (sink !== (w.known2 - 1) * w.known2 + 2 * w.known2) {
        console.error('call_known_2 checksum mismatch', sink);
      }
    }, { runs, warmup });
    tests.push(makeResult('call_known_2', samples, { iterations: w.known2 }));
  } catch (err) {
    tests.push({
      name: 'call_known_2',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => wasm.callGeneric2(w.callGeneric2), { runs, warmup });
    tests.push(makeResult('call_generic_2', samples));
  } catch (err) {
    tests.push({
      name: 'call_generic_2',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const getset0: { x?: number } = {};
    Object.defineProperty(getset0, 'x', {
      get: wasm.getGlobal0 as any,
    });
    const samples = measureWork(() => {
      for (let i = 0; i < w.getterSetter; i++) {
        const _ = getset0.x;
      }
    }, { runs, warmup });
    tests.push(makeResult('scripted_getter_0', samples));
  } catch (err) {
    tests.push({
      name: 'scripted_getter_0',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const getset1: { x?: number } = {};
    Object.defineProperty(getset1, 'x', {
      get: wasm.getGlobal1 as any,
    });
    const samples = measureWork(() => {
      for (let i = 0; i < w.getterSetter; i++) {
        const _ = getset1.x;
      }
    }, { runs, warmup });
    tests.push(makeResult('scripted_getter_1', samples));
  } catch (err) {
    tests.push({
      name: 'scripted_getter_1',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const set1: { x?: number } = {};
    Object.defineProperty(set1, 'x', {
      set: wasm.setGlobalOne as any,
    });
    const samples = measureWork(() => {
      for (let i = 0; i < w.getterSetter; i++) {
        set1.x = i;
      }
    }, { runs, warmup });
    tests.push(makeResult('scripted_setter_1', samples));
  } catch (err) {
    tests.push({
      name: 'scripted_setter_1',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const set2: { x?: number } = {};
    Object.defineProperty(set2, 'x', {
      set: wasm.setGlobalTwo as any,
    });
    const samples = measureWork(() => {
      for (let i = 0; i < w.getterSetter; i++) {
        set2.x = i;
      }
    }, { runs, warmup });
    tests.push(makeResult('scripted_setter_2', samples));
  } catch (err) {
    tests.push({
      name: 'scripted_setter_2',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      for (let i = 0; i < w.applyCall; i++) {
        (wasm.known2 as any).apply(null, [i, i + 1]);
      }
    }, { runs, warmup });
    tests.push(makeResult('F_p_apply_array', samples));
  } catch (err) {
    tests.push({
      name: 'F_p_apply_array',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      for (let i = 0; i < w.applyCall; i++) {
        (wasm.known2 as any).apply(null, [i]);
      }
    }, { runs, warmup });
    tests.push(makeResult('F_p_apply_array_r', samples));
  } catch (err) {
    tests.push({
      name: 'F_p_apply_array_r',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      for (let i = 0; i < w.applyCall; i++) {
        (wasm.known2 as any).call(null, i, i + 1);
      }
    }, { runs, warmup });
    tests.push(makeResult('F_p_call', samples));
  } catch (err) {
    tests.push({
      name: 'F_p_call',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => {
      for (let i = 0; i < w.applyCall; i++) {
        (wasm.known2 as any).call(null, i);
      }
    }, { runs, warmup });
    tests.push(makeResult('F_p_call_r', samples));
  } catch (err) {
    tests.push({
      name: 'F_p_call_r',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = measureWork(() => wasm.ifAddWasm(w.ifAdd, 1, 2), { runs, warmup });
    tests.push(makeResult('if_add_wasm', samples));
  } catch (err) {
    tests.push({
      name: 'if_add_wasm',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const ifAddJs = (iterations: number, a: number, b: number): number => {
      let sum = 0;
      for (let i = 0; i < iterations; i++) {
        if (a + 1) {
          sum += (a - 0) + (b - 0);
        }
      }
      return sum;
    };
    const samples = measureWork(() => ifAddJs(w.ifAdd, 1, 2), { runs, warmup });
    tests.push(makeResult('if_add_js', samples));
  } catch (err) {
    tests.push({
      name: 'if_add_js',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  return tests;
}
